/**
 * Split Test Results in-builder surfaces
 *
 * Shared types, constants, and pure logic for variant metrics and leader-cue
 * computation. No React in this file — keeps it testable without a DOM.
 */

import type {
    InsightRowWithRevision,
    InsightSplitTestRef,
    InsightVariantRef,
} from '../journey-notifications/revision-insights'

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Minimum impression count below which per-variant rates (CTR, Purchase Rate)
 * are considered statistically unreliable. Displayed as "—" and no leader cue
 * is suggested below this floor.
 */
export const LOW_DATA_IMPRESSION_FLOOR = 500

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * One row as returned by the challenger insights API for a variant breakdown.
 *
 * Extends the shared `InsightRowWithRevision` response shape — reusing its metric
 * fields (entrances, impressions, clicks, ctr_decimal, purchases, purchase_amount,
 * purchase_rate) and revision identity — and narrows the nested `variant`/`split_test`
 * objects that the variant breakdown always returns.
 */
export interface VariantApiRow extends InsightRowWithRevision {
    variant: InsightVariantRef & {
        id: number | string
        name: string
    }
    /**
     * The VARIANT_SPLIT this variant belongs to. `step_id` equals the graph split
     * node id, so it is the reliable key for scoping rows to one split test (warehouse
     * `variant.id` collapses to 0 across rows, and variant NAMES are only unique WITHIN
     * a split — they collide across splits with default A/B/C naming).
     */
    split_test?: InsightSplitTestRef
}

/**
 * Normalized variant row used by both the panel table and the fork chip.
 */
export type VariantMetricsRow = {
    variantId: number | string
    variantName: string
    variantPercentage: number | null
    /** Step id of the owning VARIANT_SPLIT (= graph split node id). Reliable per-split key. */
    splitTestStepId: number | string | null
    splitTestName: string | null
    entrances: number
    impressions: number
    clicks: number
    /** null means clicks === 0, undefined means API did not return the field */
    ctr: number | null | undefined
    purchases: number | null
    purchaseAmount: number | null
    purchaseRate: number | null | undefined
    /**
     * Whether this row has enough data to show rates (impressions >= LOW_DATA_IMPRESSION_FLOOR).
     * Below the floor, rate cells render "—" and no leader cue is produced.
     */
    hasSufficientData: boolean
}

/** The primary metric used for leader-cue comparison (purchase rate preferred; CTR fallback). */
export type LeaderMetric = 'purchase_rate' | 'ctr'

export type LeaderCue = {
    /**
     * The leader is keyed by the variant NAME. Names are validated unique within a
     * split, so name equality marks exactly one row — a robust display identity for
     * the leader cue that doesn't depend on how the warehouse id is typed.
     */
    variantName: string
    variantId: number | string
    metric: LeaderMetric
}

/**
 * Robust leader-equality check. A row is the leader iff the cue exists, the
 * cue's variantName is defined/non-empty, and it matches this row's name
 * (case-insensitive, trimmed). Because names are unique within a split this can
 * NEVER mark more than one row. Returns false for any ambiguous/empty identity.
 */
export function isLeaderRow(
    cue: LeaderCue | null | undefined,
    row: Pick<VariantMetricsRow, 'variantName'> | null | undefined,
): boolean {
    if (!cue || !row) return false
    const cueName = cue.variantName?.trim().toLowerCase()
    const rowName = row.variantName?.trim().toLowerCase()
    if (!cueName || !rowName) return false
    return cueName === rowName
}

// ─── Pure logic ───────────────────────────────────────────────────────────────

/** Normalize a raw API row into a VariantMetricsRow. */
export function normalizeVariantRow(raw: VariantApiRow): VariantMetricsRow {
    const impressions = raw.impressions ?? 0
    return {
        variantId: raw.variant.id,
        variantName: raw.variant.name,
        variantPercentage: raw.variant.percentage ?? null,
        splitTestStepId: raw.split_test?.step_id ?? null,
        splitTestName: raw.split_test?.name ?? null,
        entrances: raw.entrances ?? 0,
        impressions,
        clicks: raw.clicks ?? 0,
        ctr: raw.ctr_decimal,
        purchases: raw.purchases ?? null,
        purchaseAmount: raw.purchase_amount ?? null,
        purchaseRate: raw.purchase_rate ?? null,
        hasSufficientData: impressions >= LOW_DATA_IMPRESSION_FLOOR,
    }
}

/**
 * Find the metrics row that belongs to a given graph branch.
 *
 * The on-canvas fork chips render per branch edge (one per variant lane) while
 * the split still exists. Each branch carries a `key` (a stable nanoid); the
 * insights API returns rows keyed by the warehouse `variant.id`, which IS that
 * branch key, so we match on it directly.
 *
 * Returns null when no row matches (e.g. a brand-new variant with no data yet).
 */
export function findRowForBranch(
    rows: VariantMetricsRow[],
    branch: { key: string | number; label?: string | null },
): VariantMetricsRow | null {
    if (rows.length === 0) return null

    const keyStr = String(branch.key)
    return rows.find((r) => String(r.variantId) === keyStr) ?? null
}

/**
 * Determine whether purchase columns should be visible.
 *
 * Convention mirrors `journey-gen-stats-row.tsx` (hasRevenueStats):
 * data-driven — show iff at least one variant has purchases > 0.
 *
 * Note: this was validated against the existing codebase convention. The
 * journey-gen-stats-row uses `(stats.purchases ?? 0) > 0`. We apply the same
 * check across all variants here.
 */
export function shouldShowPurchaseColumns(rows: VariantMetricsRow[]): boolean {
    return rows.some((r) => (r.purchases ?? 0) > 0)
}

/**
 * Compute the leader cue, if one exists.
 *
 * Rules:
 * - Only consider variants with impressions >= LOW_DATA_IMPRESSION_FLOOR.
 * - Primary metric: purchase_rate (used iff at least one eligible variant has a
 *   non-null purchase_rate AND purchases > 0).
 * - Fallback metric: ctr (used iff purchase_rate is not usable).
 * - If all eligible variants are tied on the chosen metric, returns null (no cue).
 * - If fewer than 2 eligible variants, returns null (can't compare).
 *
 * IMPORTANT: This is a "leading" cue (subtle tag), never a "winner" stamp.
 * It is purely client-side and informational.
 */
export function computeLeaderCue(rows: VariantMetricsRow[]): LeaderCue | null {
    const eligible = rows.filter((r) => r.hasSufficientData)
    if (eligible.length < 2) return null

    // Guard: leader identity is the variant NAME. If two eligible rows share the
    // same (normalized) name — or any eligible row has no usable name — the
    // identity is ambiguous and we cannot safely attribute a single leader.
    // Returning null here guarantees we NEVER mark more than one variant.
    const seenNames = new Set<string>()
    for (const r of eligible) {
        const name = r.variantName?.trim().toLowerCase()
        if (!name) return null
        if (seenNames.has(name)) return null
        seenNames.add(name)
    }

    const cueFor = (row: VariantMetricsRow, metric: LeaderMetric): LeaderCue => ({
        variantName: row.variantName,
        variantId: row.variantId,
        metric,
    })

    // Try purchase_rate first (only when at least one eligible row has purchases > 0)
    const hasPurchaseData = eligible.some((r) => (r.purchases ?? 0) > 0)
    if (hasPurchaseData) {
        const best = maxByNullable(eligible, (r) => r.purchaseRate)
        if (best) {
            return cueFor(best, 'purchase_rate')
        }
    }

    // Fallback: CTR
    const bestCtr = maxByNullable(eligible, (r) => r.ctr)
    if (bestCtr) {
        return cueFor(bestCtr, 'ctr')
    }

    return null
}

/** A graph branch reference used to order/resolve metrics rows within a split. */
export type SplitBranchRef = { key: string | number; label?: string | null }

/**
 * The rows belonging to ONE split test, scoped by the split's step id
 * (= graph VARIANT_SPLIT node id). This is the reliable key: a journey can hold
 * several independent split tests, warehouse `variant.id` collapses to 0, and
 * variant NAMES are only unique within a split (default A/B/C collide across
 * splits). Scoping by step id avoids attributing one split's rows to another.
 */
export function getRowsForSplit(
    splitStepId: string | number,
    rows: VariantMetricsRow[],
): VariantMetricsRow[] {
    const key = String(splitStepId)
    return rows.filter((r) => r.splitTestStepId != null && String(r.splitTestStepId) === key)
}

/**
 * Order a split's rows to match the canvas lane order (branch order). Within a
 * single split, variant names are unique, so each branch resolves to one row.
 */
export function orderRowsByBranches(
    rows: VariantMetricsRow[],
    branches: SplitBranchRef[],
): VariantMetricsRow[] {
    const out: VariantMetricsRow[] = []
    const seen = new Set<VariantMetricsRow>()
    for (const branch of branches) {
        const row = findRowForBranch(rows, branch)
        if (row && !seen.has(row)) {
            seen.add(row)
            out.push(row)
        }
    }
    // Append any scoped rows a branch didn't resolve to (defensive — shouldn't happen).
    for (const r of rows) if (!seen.has(r)) out.push(r)
    return out
}

/**
 * Compute the leader cue PER split test, keyed by the split node's graph step id.
 *
 * A journey may have multiple independent split tests; each must surface its own
 * leader (computeLeaderCue across only that split's variants, scoped by step id).
 * A single cue across all variants would mark only the one global-best variant and
 * leave every other split with no leader.
 */
export function computeLeaderCuesBySplit(
    splitStepIds: (string | number)[],
    rows: VariantMetricsRow[],
): Record<string, LeaderCue | null> {
    const bySplit: Record<string, LeaderCue | null> = {}
    for (const splitStepId of splitStepIds) {
        bySplit[String(splitStepId)] = computeLeaderCue(getRowsForSplit(splitStepId, rows))
    }
    return bySplit
}

/**
 * Return the row with the strictly highest value for the given accessor.
 * Returns null if all values are null/undefined, or if there is a tie.
 */
function maxByNullable<T>(
    items: T[],
    accessor: (item: T) => number | null | undefined,
): T | null {
    let bestValue: number | null = null
    let bestItem: T | null = null
    let tied = false

    for (const item of items) {
        const val = accessor(item)
        if (val == null) continue

        if (bestValue === null) {
            bestValue = val
            bestItem = item
            tied = false
        } else if (val > bestValue) {
            bestValue = val
            bestItem = item
            tied = false
        } else if (val === bestValue) {
            tied = true
        }
    }

    if (tied || bestItem === null) return null
    return bestItem
}
